export interface DiffLine {
  kind: 'ctx' | 'add' | 'del'
  text: string
}

export const LINE_DIFF_MAX_LINES = 5_000
/** The LCS table is (n+1)·(m+1) 32-bit cells; 4M cells is 16 MB, plenty for any script. */
export const LINE_DIFF_MAX_CELLS = 4_000_000

/**
 * A plain LCS line diff for a script a few hundred lines long: the builder's
 * diff carries the whole `parser.script` before and after, and the tab wants
 * `- / +` lines. Inputs beyond LINE_DIFF_MAX_LINES on either side, or whose
 * table would exceed LINE_DIFF_MAX_CELLS, fall back to "everything changed"
 * rather than an O(n·m) table.
 */
export function lineDiff(before: string, after: string): DiffLine[] {
  const a = splitLines(before)
  const b = splitLines(after)
  if (
    a.length > LINE_DIFF_MAX_LINES ||
    b.length > LINE_DIFF_MAX_LINES ||
    (a.length + 1) * (b.length + 1) > LINE_DIFF_MAX_CELLS
  ) {
    return [
      ...a.map((text) => ({ kind: 'del' as const, text })),
      ...b.map((text) => ({ kind: 'add' as const, text }))
    ]
  }
  // lcs[i][j] = length of the LCS of a[i..] and b[j..]
  const lcs: Uint32Array[] = Array.from(
    { length: a.length + 1 },
    () => new Uint32Array(b.length + 1)
  )
  for (let i = a.length - 1; i >= 0; i -= 1) {
    const row = lcs[i]!
    const next = lcs[i + 1]!
    for (let j = b.length - 1; j >= 0; j -= 1) {
      row[j] = a[i] === b[j] ? next[j + 1]! + 1 : Math.max(next[j]!, row[j + 1]!)
    }
  }
  const out: DiffLine[] = []
  let i = 0
  let j = 0
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      out.push({ kind: 'ctx', text: a[i]! })
      i += 1
      j += 1
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) {
      out.push({ kind: 'del', text: a[i]! })
      i += 1
    } else {
      out.push({ kind: 'add', text: b[j]! })
      j += 1
    }
  }
  for (; i < a.length; i += 1) out.push({ kind: 'del', text: a[i]! })
  for (; j < b.length; j += 1) out.push({ kind: 'add', text: b[j]! })
  return out
}

export type DiffHunk = { kind: 'lines'; lines: DiffLine[] } | { kind: 'collapsed'; count: number }

/**
 * Fold unchanged runs longer than `2 * context` behind an "n unchanged lines"
 * marker, keeping `context` lines on either side of every change.
 */
export function collapseContext(lines: DiffLine[], context = 2): DiffHunk[] {
  const hunks: DiffHunk[] = []
  let run: DiffLine[] = []
  let visible: DiffLine[] = []
  const flushRun = (atEnd: boolean) => {
    if (run.length === 0) return
    const keepHead = visible.length === 0 && hunks.length === 0 ? 0 : context
    const keepTail = atEnd ? 0 : context
    if (run.length > keepHead + keepTail) {
      visible.push(...run.slice(0, keepHead))
      if (visible.length) hunks.push({ kind: 'lines', lines: visible })
      hunks.push({ kind: 'collapsed', count: run.length - keepHead - keepTail })
      visible = keepTail ? run.slice(run.length - keepTail) : []
    } else {
      visible.push(...run)
    }
    run = []
  }
  for (const line of lines) {
    if (line.kind === 'ctx') {
      run.push(line)
    } else {
      flushRun(false)
      visible.push(line)
    }
  }
  flushRun(true)
  if (visible.length) hunks.push({ kind: 'lines', lines: visible })
  return hunks
}

function splitLines(text: string): string[] {
  if (text === '') return []
  const lines = text.split('\n')
  if (lines[lines.length - 1] === '') lines.pop()
  return lines
}
